import { Controller } from "@hotwired/stimulus";
import intlTelInput from "intl-tel-input";

// Maps intl-tel-input validation error codes to user-facing Spanish messages.
// See https://intl-tel-input.com/docs/best-practices
const ERROR_MESSAGES = {
  INVALID_COUNTRY_CODE: "Código de país inválido",
  TOO_SHORT: "Número demasiado corto",
  TOO_LONG: "Número demasiado largo",
  IS_POSSIBLE_LOCAL_ONLY: "Falta el código de área",
  INVALID_LENGTH: "Longitud de número inválida",
};

export default class extends Controller {
  static targets = ["input", "full", "country", "error"];

  connect() {
    this.beforeCache = () => this.teardown();
    this.afterRender = () => this.setup();

    document.addEventListener("turbo:before-cache", this.beforeCache);
    document.addEventListener("turbo:render", this.afterRender);

    // Find parent form and sync/validate before submit
    this.form = this.element.closest("form");
    if (this.form) {
      this.beforeSubmit = (e) => this.syncBeforeSubmit(e);
      this.form.addEventListener("submit", this.beforeSubmit);
    }

    // Initialize if input target already exists
    if (this.hasInputTarget) {
      this.setup();
    }
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this.beforeCache);
    document.removeEventListener("turbo:render", this.afterRender);

    if (this.form && this.beforeSubmit) {
      this.form.removeEventListener("submit", this.beforeSubmit);
    }

    this.teardown();
  }

  // Called when Turbo morph inserts/replaces the input target
  inputTargetConnected() {
    this.setup();
  }

  // Called when Turbo morph removes/replaces the input target
  inputTargetDisconnected() {
    this.teardown();
  }

  setup() {
    this.teardown();

    // On a hard refresh the JS module can run before stylesheets and fonts
    // have settled, which makes intl-tel-input measure the country container
    // too narrowly (the flag and dial code end up overlapping the typed
    // number). Defer initialization until the page has fully loaded so the
    // measurement is taken against the final layout.
    if (document.readyState !== "complete") {
      this.loadHandler = () => {
        this.loadHandler = null;
        if (this.hasInputTarget) this.setup();
      };
      window.addEventListener("load", this.loadHandler, { once: true });
      return;
    }

    // Capture existing hidden field values before init (for re-renders with validation errors)
    const existingCountryValue = this.hasCountryTarget
      ? this.countryTarget.value
      : "";

    // Best practice: strictMode (default) caps length and rejects non-numeric
    // input as the user types, with a built-in shake on reject.
    this.iti = intlTelInput(this.inputTarget, {
      loadUtils: () => import("intl-tel-input/utils"),
      initialCountry: existingCountryValue || "do",
      separateDialCode: true,
      placeholderNumberPolicy: "OFF",
      strictMode: true,
    });

    // separateDialCode pads the input inline to clear the flag + dial code.
    // Re-assert it (see promotePadding) in case a padding utility would win.
    this.promotePadding();

    // getNumber()/isValidNumber() need the utils module. Track when it lands.
    this.utilsReady = false;
    this.iti.promise.then(() => {
      this.utilsReady = true;
      this.sync();
      this.promotePadding();
    });

    this.onInput = () => {
      this.sync();
      this.clearError();
      this.promotePadding();
    };
    this.onBlur = () => this.validate();

    this.inputTarget.addEventListener("input", this.onInput);
    this.inputTarget.addEventListener("blur", this.onBlur);
    this.inputTarget.addEventListener("countrychange", this.onInput);
  }

  teardown() {
    if (this.loadHandler) {
      window.removeEventListener("load", this.loadHandler);
      this.loadHandler = null;
    }

    if (this.hasInputTarget && this.onInput) {
      this.inputTarget.removeEventListener("input", this.onInput);
      this.inputTarget.removeEventListener("blur", this.onBlur);
      this.inputTarget.removeEventListener("countrychange", this.onInput);
    }
    this.onInput = null;
    this.onBlur = null;
    this.utilsReady = false;

    this.clearError();
    if (this.injectedError) {
      this.injectedError.remove();
      this.injectedError = null;
    }

    if (this.iti) {
      // Drop the padding we promoted so a torn-down (e.g. Turbo-cached) input
      // doesn't keep stale spacing; destroy() then restores the original.
      if (this.hasInputTarget) {
        for (const prop of [
          "padding-left",
          "padding-right",
          "padding-inline-start",
          "padding-inline-end",
        ]) {
          this.inputTarget.style.removeProperty(prop);
        }
      }

      this.iti.destroy();
      this.iti = null;
    }
  }

  // Mirror the parsed number into hidden fields when they exist. Apps with a
  // hidden `full` target keep the visible input in national format and store
  // E.164 separately; single-column apps have no hidden fields and instead get
  // their E.164 value written into the visible input at submit time.
  sync() {
    if (!this.iti) return;

    if (this.hasCountryTarget) {
      this.countryTarget.value = this.iti.getSelectedCountry()?.iso2 || "";
    }

    if (this.hasFullTarget && this.utilsReady) {
      this.fullTarget.value = this.iti.getNumber() || "";
    }
  }

  // intl-tel-input sets the input's horizontal padding inline (no !important)
  // to clear the flag/dial code. Some apps run Tailwind in `important` mode,
  // where a padding utility on the field (p-2, px-3, c-form-input, ...) carries
  // !important and would override that, leaving the flag and dial code
  // overlapping the number. Re-assert the widget's padding with !important so
  // it wins (an inline !important beats a selector's !important). No-op where
  // utilities aren't !important.
  promotePadding() {
    if (!this.iti) return;

    const style = this.inputTarget.style;
    // Cover both physical (p-2 -> padding) and logical (px-3 -> padding-inline)
    // utility forms by asserting each physical edge and its logical equivalent.
    const pairs = [
      ["padding-left", "padding-inline-start"],
      ["padding-right", "padding-inline-end"],
    ];
    for (const [physical, logical] of pairs) {
      const value = style.getPropertyValue(physical);
      if (value) {
        style.setProperty(physical, value, "important");
        style.setProperty(logical, value, "important");
      }
    }
  }

  // Best practice: validate with isValidNumber and surface a message. An empty
  // value is treated as valid here (presence is enforced server-side).
  validate() {
    if (!this.iti || !this.utilsReady) return true;

    if (this.inputTarget.value.trim() === "") {
      this.clearError();
      return true;
    }

    if (this.iti.isValidNumber() === false) {
      const code = this.iti.getValidationError();
      this.showError(ERROR_MESSAGES[code] || "Número inválido");
      return false;
    }

    this.clearError();
    return true;
  }

  syncBeforeSubmit(e) {
    // Defer submit until utils have loaded so we can validate and format.
    if (this.iti && !this.utilsReady) {
      e.preventDefault();
      this.iti.promise.then(() => this.completeSubmit());
      return;
    }

    this.sync();

    if (!this.validate()) {
      e.preventDefault();
      this.inputTarget.focus();
      return;
    }

    this.writeBackNumber();
  }

  completeSubmit() {
    this.sync();

    if (!this.validate()) {
      this.inputTarget.focus();
      return;
    }

    this.writeBackNumber();

    if (typeof this.form.requestSubmit === "function") {
      this.form.requestSubmit();
    } else {
      this.form.submit();
    }
  }

  // Single-column apps: post the E.164 number, not the formatted display value.
  writeBackNumber() {
    if (this.hasFullTarget || !this.iti) return;

    const number = this.iti.getNumber();
    if (number) this.inputTarget.value = number;
  }

  // Use an explicit [data-phone-target="error"] element if the form provides
  // one; otherwise inject a message element so forms need no extra markup.
  errorElement() {
    if (this.hasErrorTarget) return this.errorTarget;

    if (!this.injectedError) {
      const el = document.createElement("p");
      el.style.marginTop = "0.25rem";
      el.style.fontSize = "0.875rem";
      el.style.color = "#dc2626";
      el.hidden = true;
      this.element.appendChild(el);
      this.injectedError = el;
    }

    return this.injectedError;
  }

  showError(message) {
    const el = this.errorElement();
    el.textContent = message;
    el.hidden = false;
  }

  clearError() {
    const el = this.hasErrorTarget ? this.errorTarget : this.injectedError;
    if (el) {
      el.textContent = "";
      el.hidden = true;
    }
  }
}
